import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Info } from "lucide-react"
import type { Integration } from "@prescreen/lib/types"
import {
  DEFAULT_BANKING_CAPABILITIES,
  BANKING_SCOPE,
  type BankingCapabilities,
  type BankingPermissionTier,
} from "@prescreen/lib/types"
import { PermissionTierTrack } from "@/components/PermissionTierTrack"
import {
  AdminApiBankingPermissionRows,
  JxchangeBankingPermissionRows,
} from "@/components/SilverlakeBankingCapabilitiesPickers"
import {
  integrationHasJackHenrySilverlake,
  integrationHasJackHenrySynergy,
  integrationHasProvider,
} from "@/lib/oauth-secret-scope-builder"
import { useMemo } from "react"

export type SecretPermissionsFormProps = {
  idPrefix: string
  integration: Integration
  bankingCapabilities: BankingCapabilities
  onBankingTierChange: (key: keyof BankingCapabilities, tier: BankingPermissionTier) => void
  synergyDocumentsTier: BankingPermissionTier
  onSynergyDocumentsTierChange: (tier: BankingPermissionTier) => void
  prequalTier: BankingPermissionTier
  onPrequalTierChange: (tier: BankingPermissionTier) => void
  hardpullTier: BankingPermissionTier
  onHardpullTierChange: (tier: BankingPermissionTier) => void
}

function PermissionFieldRow({
  htmlFor,
  labelledBy,
  label,
  tooltip,
  value,
  onChange,
}: {
  htmlFor: string
  labelledBy: string
  label: string
  tooltip: string
  value: BankingPermissionTier
  onChange: (t: BankingPermissionTier) => void
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:min-w-[200px] sm:max-w-md">
        <span id={htmlFor} className="text-sm font-normal leading-snug text-foreground">
          {label}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={`More about ${label} permissions`}
            >
              <Info className="size-4" strokeWidth={1.5} aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="start"
            className="max-w-xs text-left text-xs leading-snug"
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      <PermissionTierTrack labelledBy={labelledBy} value={value} onChange={onChange} />
    </div>
  )
}

export function SecretPermissionsForm({
  idPrefix,
  integration,
  bankingCapabilities,
  onBankingTierChange,
  synergyDocumentsTier,
  onSynergyDocumentsTierChange,
  prequalTier,
  onPrequalTierChange,
  hardpullTier,
  onHardpullTierChange,
}: SecretPermissionsFormProps) {
  const hasSilverlake = integrationHasJackHenrySilverlake(integration)
  const hasSynergy = integrationHasJackHenrySynergy(integration)
  const hasPrequal = integrationHasProvider(integration, "equifax_prequal")
  const hasDit = integrationHasProvider(integration, "equifax_dit")
  const hasCredit = hasPrequal || hasDit

  const tabItems = useMemo(() => {
    const items: { id: string; label: string }[] = []
    if (hasSilverlake) {
      items.push({ id: "jx", label: "JXChange" })
      items.push({ id: "admin", label: "Admin API" })
    }
    if (hasSynergy) {
      items.push({ id: "synergy", label: "Synergy" })
    }
    if (hasCredit) {
      items.push({ id: "credit", label: "Credit" })
    }
    return items
  }, [hasSilverlake, hasSynergy, hasCredit])

  const defaultTab = tabItems[0]?.id ?? "credit"

  const synergyRow = (
    <PermissionFieldRow
      htmlFor={`${idPrefix}-synergy-docs-label`}
      labelledBy={`${idPrefix}-synergy-docs-label`}
      label="Synergy documents"
      tooltip={`Read: ${BANKING_SCOPE.DOCUMENTS_READ}. Write also includes ${BANKING_SCOPE.DOCUMENTS_WRITE}.`}
      value={synergyDocumentsTier}
      onChange={onSynergyDocumentsTierChange}
    />
  )

  const creditRows = (
    <div className="space-y-3">
      {hasPrequal ? (
        <PermissionFieldRow
          htmlFor={`${idPrefix}-prequal-label`}
          labelledBy={`${idPrefix}-prequal-label`}
          label="Prequalification"
          tooltip={`Read: ${BANKING_SCOPE.PREQUAL_READ}. Write also includes ${BANKING_SCOPE.PREQUAL_WRITE}. Covers creating and polling prescreen requests. DIT that runs during borrower submit uses the integration Equifax config, not a hard-pull scope.`}
          value={prequalTier}
          onChange={onPrequalTierChange}
        />
      ) : null}
      {hasDit ? (
        <PermissionFieldRow
          htmlFor={`${idPrefix}-hardpull-label`}
          labelledBy={`${idPrefix}-hardpull-label`}
          label="Hard pull"
          tooltip={`Read: ${BANKING_SCOPE.HARDPULL_READ}. Write also includes ${BANKING_SCOPE.HARDPULL_WRITE}. Required for partner DIT / hard-pull APIs.`}
          value={hardpullTier}
          onChange={onHardpullTierChange}
        />
      ) : null}
    </div>
  )

  if (tabItems.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This integration has no OAuth products configured. Add Equifax prequalification, Equifax DIT, Jack Henry
        Silverlake, or Synergy before creating a secret.
      </p>
    )
  }

  if (tabItems.length === 1 && tabItems[0].id === "synergy") {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">Synergy documents</p>
        {synergyRow}
      </div>
    )
  }

  if (tabItems.length === 1 && tabItems[0].id === "credit") {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">Credit</p>
        {creditRows}
      </div>
    )
  }

  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <TabsList className="mb-3 h-auto w-full flex-wrap justify-start gap-1">
        {tabItems.map((t) => (
          <TabsTrigger key={t.id} value={t.id} className="shrink-0">
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {hasSilverlake ? (
        <>
          <TabsContent value="jx" className="mt-0 space-y-3">
            <JxchangeBankingPermissionRows
              idPrefix={`${idPrefix}-jx`}
              capabilities={bankingCapabilities}
              onTierChange={onBankingTierChange}
            />
          </TabsContent>
          <TabsContent value="admin" className="mt-0 space-y-3">
            <AdminApiBankingPermissionRows
              idPrefix={`${idPrefix}-admin`}
              capabilities={bankingCapabilities}
              onTierChange={onBankingTierChange}
            />
          </TabsContent>
        </>
      ) : null}
      {hasSynergy ? (
        <TabsContent value="synergy" className="mt-0 space-y-3">
          {synergyRow}
        </TabsContent>
      ) : null}
      {hasCredit ? (
        <TabsContent value="credit" className="mt-0 space-y-3">
          {creditRows}
        </TabsContent>
      ) : null}
    </Tabs>
  )
}

export { DEFAULT_BANKING_CAPABILITIES }
